/**
 * RecentProgress — Dashboard progress overview
 *
 * Always shows a compact glanceable strip. Full analytics (metrics,
 * badges, readiness gauge, trend, milestones) live behind a collapse
 * so the dashboard stays action-first.
 *
 * Data sources:
 *   api.progress.getDashboard()
 *   api.progress.getReadinessScore()
 *   api.progress.getBadgesV2()
 *   api.progress.getReadinessHistory()
 *   api.progress.getMilestones()
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  Skeleton,
  Typography,
} from '@mui/material';
import {
  EmojiEvents,
  Assessment,
  TrendingUp,
  WorkspacePremium,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import api from '../api/client';
import BadgeGrid from './progress/BadgeGrid';
import InterviewReadinessScore from './progress/InterviewReadinessScore';
import MilestoneTimeline from './progress/MilestoneTimeline';
import ReadinessTrendChart from './progress/ReadinessTrendChart';

/** Show trend only when there is enough non-trivial history to read. */
const hasUsefulTrend = (history = []) => {
  const meaningful = history.filter((h) => Number(h?.score) > 0);
  return meaningful.length >= 3;
};

const RecentProgress = ({ onViewProgress, defaultExpanded = false }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [stats, setStats] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [badges, setBadges] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [readinessHistory, setReadinessHistory] = useState([]);
  const [milestones, setMilestones] = useState([]);

  useEffect(() => {
    let cancelled = false;

    const fetchProgress = async () => {
      try {
        setLoading(true);

        const [dashRes, ...optional] = await Promise.allSettled([
          api.progress.getDashboard(),
          api.progress.getReadinessScore(),
          api.progress.getBadgesV2(),
          api.progress.getReadinessHistory({ limit: 30 }),
          api.progress.getMilestones(),
        ]);

        if (cancelled) return;

        if (dashRes.status !== 'fulfilled') {
          setError(true);
          return;
        }

        setStats(dashRes.value?.data?.stats || null);

        if (optional[0]?.status === 'fulfilled') {
          setReadiness(optional[0].value?.data || null);
        }

        if (optional[1]?.status === 'fulfilled') {
          const fetchedBadges = optional[1].value?.data?.badges || [];
          setAllBadges(fetchedBadges);
          const earned = fetchedBadges
            .filter((b) => b.earned)
            .sort((a, b) => new Date(b.earned_at) - new Date(a.earned_at))
            .slice(0, 5);
          setBadges(earned);
        }

        if (optional[2]?.status === 'fulfilled') {
          setReadinessHistory(optional[2].value?.data?.history || []);
        }

        if (optional[3]?.status === 'fulfilled') {
          setMilestones(optional[3].value?.data?.milestones || []);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProgress();
    return () => { cancelled = true; };
  }, []);

  if (error) return null;

  if (loading) {
    return (
      <Box sx={{ mb: 3 }} data-testid="recent-progress-loading">
        <Skeleton variant="rounded" height={56} />
      </Box>
    );
  }

  if (!stats) return null;

  const totalInterviews = stats.total_interviews || 0;
  const averageScoreRaw = stats.average_score != null && stats.average_score !== ''
    ? parseFloat(stats.average_score)
    : null;
  const averageScoreLabel = averageScoreRaw != null && !Number.isNaN(averageScoreRaw)
    ? `${averageScoreRaw.toFixed(1)}/10`
    : '—';
  const readinessScore = readiness?.score ?? null;
  const readinessComponents = readiness?.components || {};
  const showTrend = hasUsefulTrend(readinessHistory);
  const change = readiness?.change;

  const summaryParts = [
    readinessScore != null ? `Readiness ${Math.round(readinessScore)}` : null,
    `${totalInterviews} interview${totalInterviews === 1 ? '' : 's'}`,
    averageScoreRaw != null ? `Avg ${averageScoreLabel}` : null,
  ].filter(Boolean);

  return (
    <Box sx={{ mb: 3 }} data-testid="recent-progress">
      {/* Compact strip — always visible */}
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
          py: 1.25,
          px: { xs: 1.5, sm: 2 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
        data-testid="recent-progress-summary"
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0, flex: 1 }}>
          <TrendingUp color="primary" sx={{ mt: 0.25, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.primary">
              Progress overview
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              data-testid="recent-progress-summary-text"
              sx={{ lineHeight: 1.4 }}
            >
              {summaryParts.join(' · ')}
              {typeof change === 'number' && change !== 0 && (
                <Box component="span" sx={{ color: change > 0 ? 'success.main' : 'text.secondary' }}>
                  {` · ${change > 0 ? '+' : ''}${change} this week`}
                </Box>
              )}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          {onViewProgress && (
            <Button
              size="small"
              onClick={onViewProgress}
              sx={{ textTransform: 'none' }}
              data-testid="recent-progress-full-link"
            >
              Full progress
            </Button>
          )}
          <Button
            size="small"
            onClick={() => setExpanded((v) => !v)}
            endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: 'none' }}
            data-testid="recent-progress-toggle"
            aria-expanded={expanded}
          >
            {expanded ? 'Hide details' : 'Details'}
          </Button>
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 2 }} data-testid="recent-progress-details">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: '100%', borderLeft: 4, borderColor: 'primary.main' }}
                data-testid="recent-progress-metrics"
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Assessment color="primary" />
                    <Typography variant="h6" fontWeight={700}>
                      Key Metrics
                    </Typography>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', py: 1 }}>
                        <Typography variant="h3" color="primary.main" fontWeight={700}>
                          {totalInterviews}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Interviews Completed
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={6}>
                      <Box sx={{ textAlign: 'center', py: 1 }}>
                        <Typography variant="h3" color="text.primary" fontWeight={700}>
                          {averageScoreLabel}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Average Score
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card
                variant="outlined"
                sx={{ height: '100%', borderLeft: 4, borderColor: 'warning.main' }}
                data-testid="recent-progress-achievements"
              >
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <EmojiEvents sx={{ color: 'warning.main' }} />
                    <Typography variant="h6" fontWeight={700}>
                      Recent Achievements
                    </Typography>
                  </Box>

                  {badges.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {badges.map((badge) => (
                        <Chip
                          key={badge.badge_key || badge.name}
                          icon={<WorkspacePremium />}
                          label={badge.name}
                          variant="outlined"
                          color="warning"
                          sx={{ fontWeight: 600 }}
                          data-testid="badge-chip"
                        />
                      ))}
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 3,
                      }}
                    >
                      <EmojiEvents sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        Your latest badges will appear here.
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5 }}>
                        Complete interviews to earn achievements!
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={showTrend ? 4 : 12} data-testid="recent-progress-readiness">
              <InterviewReadinessScore
                score={readinessScore ?? 0}
                components={readinessComponents}
              />
            </Grid>

            {showTrend && (
              <Grid item xs={12} md={8} data-testid="recent-progress-trend">
                <ReadinessTrendChart history={readinessHistory} />
              </Grid>
            )}

            <Grid item xs={12} data-testid="recent-progress-milestones">
              <MilestoneTimeline milestones={milestones} />
            </Grid>

            <Grid item xs={12} data-testid="recent-progress-badge-grid">
              <BadgeGrid
                badges={allBadges}
                totalEarned={allBadges.filter((b) => b.earned).length}
                totalAvailable={allBadges.length}
              />
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
};

export default RecentProgress;
